import React, { useState, useEffect } from 'react';
import { useGameSession } from '../../contexts/GameSessionProvider';
import { useWallet } from '../../contexts/WalletProvider';
import { HowItWorksModal } from '../Modal';
import { getAssetColorPalette } from '../../utils/assetHelpers';
import './GameSessionControls.css';

interface GameSessionControlsProps {
  hasBoost?: boolean; // Boost applies to current session (unused OR used in this session)
  boostValue?: number;
}

/**
 * GameSessionControls Component
 * 
 * Manages the game session lifecycle UI:
 * - Pre-game: Start button with bet selection
 * - Active game: Shows progress, ladder, current round
 * - Results: Cash out vs Continue decision
 */
const GameSessionControls: React.FC<GameSessionControlsProps> = ({
  hasBoost = false,
  boostValue = 1.5
}) => {
  const { address, isAuthenticated, balance, canAfford } = useWallet();
  const {
    currentSession,
    currentRound,
    payoutLadder,
    raceMatchup,
    isLoading,
    error,
    createSession,
    pickAsset,
    cashOut,
    continueGame,
    clearError
  } = useGameSession();

  // Local state for bet amount selection
  const [selectedBetAmount, setSelectedBetAmount] = useState<number>(0.01);
  
  // Local state for How It Works modal
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  
  // ✅ Local loading states for instant UI feedback (bypasses context delays)
  const [pickingAsset, setPickingAsset] = useState<string | null>(null);
  const [isCashingOut, setIsCashingOut] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  
  // Bet amount options (in SOL) - keep it simple with 3 options
  const betAmounts = [0.005, 0.01, 0.05];
  
  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  // Check if we're in demo mode (no active session)
  const isDemoMode = !currentSession;

  // Check if we're in results phase with a win
  const isResultsPhase = currentRound?.phase === 'RESULTS';
  const playerWon = currentRound?.player_won;
  // Busted or cashed out - both show "Play Again" in controls
  const isBusted = (isResultsPhase && !playerWon) || currentSession?.session_status === 'busted' || currentSession?.session_status === 'cashed_out';
  
  // Check if we're in betting/racing phases
  const isBettingPhase = currentRound?.phase === 'BETTING';
  const isRacingPhase = currentRound?.phase === 'RACING' || currentRound?.phase === 'SETTLEMENT';
  
  // Get the race assets from raceMatchup
  const champion = raceMatchup?.champion;
  const challenger = raceMatchup?.challenger;
  const playerPick = currentRound?.player_pick;

  // Get asset brand colors from centralized utility
  const getAssetColors = getAssetColorPalette;

  // Get current payout from ladder (what you've already earned)
  const getCurrentPayout = () => {
    if (!currentSession || !payoutLadder) return null;
    
    const roundsCompleted = currentSession.rounds_completed || 0;
    const initialBet = currentSession.initial_bet || 0;
    
    // If no rounds completed yet, current payout is 0
    if (roundsCompleted === 0) return null;
    
    // Find matching entry in ladder for rounds already completed
    const entry = payoutLadder.ladder.find((e: any) => e.round === roundsCompleted);
    if (!entry) return null;
    
    // Apply boost for Round 2 if boost applies to current session
    let multiplier = entry.multiplier;
    if (entry.round === 2 && hasBoost) {
      multiplier = parseFloat((multiplier * boostValue).toFixed(2));
    }
    
    return {
      round: entry.round,
      multiplier: multiplier,
      payout: (initialBet * multiplier).toFixed(3)
    };
  };

  // Get next round payout from ladder (what you're playing for)
  const getNextPayout = () => {
    if (!currentSession || !payoutLadder) return null;
    
    const roundsCompleted = currentSession.rounds_completed || 0;
    const initialBet = currentSession.initial_bet || 0;
    
    // Find next entry in ladder: you're playing for round (roundsCompleted + 1)
    // rounds_completed = 2 means you've won 2, now playing round 3
    const nextEntry = payoutLadder.ladder.find((e: any) => e.round === roundsCompleted + 1);
    if (!nextEntry) return null;
    
    // Apply boost for Level 2 (round 2) if boost applies to current session
    let multiplier = nextEntry.multiplier;
    if (nextEntry.round === 2 && hasBoost) {
      multiplier = parseFloat((multiplier * boostValue).toFixed(2));
    }
    
    return {
      round: nextEntry.round,
      multiplier: multiplier,
      payout: (initialBet * multiplier).toFixed(3)
    };
  };

  const currentPayout = getCurrentPayout();
  const nextPayout = getNextPayout();

  // Handle starting a new game session
  const handleStartGame = async () => {
    if (!isAuthenticated || !address) {
      alert('Please connect your wallet first');
      return;
    }
    
    // Check if user can afford the selected bet amount
    if (!canAfford(selectedBetAmount)) {
      alert(`Insufficient balance. You need at least ${selectedBetAmount} SOL (plus fees) to start the game.`);
      return;
    }
    
    await createSession(selectedBetAmount);
  };

  // Handle cash out with instant feedback
  const handleCashOut = async () => {
    setIsCashingOut(true); // ✅ INSTANT visual feedback
    try {
      await cashOut();
    } finally {
      setIsCashingOut(false);
    }
  };

  // Handle continue with instant feedback
  const handleContinue = async () => {
    setIsContinuing(true); // ✅ INSTANT visual feedback
    try {
      await continueGame();
    } finally {
      setIsContinuing(false);
    }
  };

  // Handle "How It Works" button
  const handleHowItWorks = () => {
    setShowHowItWorks(true);
  };

  // Handle asset pick with instant feedback
  const handlePickAsset = async (asset: string) => {
    if (playerPick || isLoading || pickingAsset) {
      return; // ✅ Prevent double-clicks
    }
    
    setPickingAsset(asset); // ✅ INSTANT visual feedback
    try {
      await pickAsset(asset);
    } finally {
      setPickingAsset(null);
    }
  };

  // Determine if bet amount is locked (game in progress)
  const isBetLocked = currentSession && !isBusted;
  // When busted (includes cashed out) or demo mode, use selectedBetAmount. Otherwise use session's initial_bet
  const displayBetAmount = (isBusted || isDemoMode) ? selectedBetAmount : (currentSession?.initial_bet || selectedBetAmount);
  
  // Determine label text
  const betLabel = isBetLocked ? 'WAGER SET:' : 'SET WAGER:';

  return (
    <div className="game-session-controls">
      {/* Bet Amount Row - Always Visible */}
      <div className={`bet-amount-row ${isBetLocked ? 'locked' : 'active'}`}>
        <span className="bet-label">{betLabel}</span>
        <div className="bet-toggles">
          {betAmounts.map(amount => {
            const canAffordAmount = canAfford(amount);
            return (
              <button
                key={amount}
                className={`bet-toggle ${displayBetAmount === amount ? 'selected' : ''} ${!canAffordAmount && !isBetLocked ? 'insufficient-balance' : ''}`}
                onClick={() => !isBetLocked && canAffordAmount && setSelectedBetAmount(amount)}
                disabled={!!isBetLocked || !canAffordAmount}
                title={!canAffordAmount && !isBetLocked ? `Insufficient balance (${balance.toFixed(4)} SOL available)` : ''}
              >
                <img 
                  src="/assets/icons/solanaLogoMark.svg" 
                  alt="SOL" 
                  className="bet-sol-icon"
                />
                <span>{amount}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Action Buttons - Always 2 Buttons */}
      {isDemoMode && payoutLadder && (
        <div className="action-buttons decision">
          <button
            className="action-button info"
            onClick={handleHowItWorks}
          >
            <span className="button-label">How To Play</span>
          </button>
          <button
            className="action-button start"
            onClick={handleStartGame}
            disabled={isLoading || !isAuthenticated || !canAfford(selectedBetAmount)}
            title={!canAfford(selectedBetAmount) && isAuthenticated ? `Insufficient balance (${balance.toFixed(4)} SOL available)` : ''}
          >
            {isLoading ? (
              <span className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
            ) : !isAuthenticated ? (
              <span className="button-label">Connect Wallet</span>
            ) : !canAfford(selectedBetAmount) ? (
              <span className="button-label">Insufficient SOL</span>
            ) : (
              <>
                <span className="button-label">Start Game</span>
                <div className="button-multiplier-payout">
                  <span className="multiplier-text">{payoutLadder.ladder[0].multiplier}x</span>
                  <span className="separator">•</span>
                  <img 
                    src="/assets/icons/solanaLogoMark.svg" 
                    alt="SOL" 
                    className="button-sol-icon"
                  />
                  <span className="payout-text">{(selectedBetAmount * payoutLadder.ladder[0].multiplier).toFixed(3)}</span>
                </div>
              </>
            )}
          </button>
        </div>
      )}

      {/* Betting/Racing Phase: Asset Selection */}
      {(isBettingPhase || isRacingPhase) && champion && challenger && (
        <div className="action-buttons decision">
          <button
            className={`action-button racer-select ${playerPick === champion ? 'selected' : ''} ${(isLoading || pickingAsset) ? 'processing' : ''}`}
            onClick={() => handlePickAsset(champion)}
            disabled={!!playerPick || isLoading || !!pickingAsset}
            style={{
              '--asset-primary': getAssetColors(champion).primary,
              '--asset-secondary': getAssetColors(champion).secondary,
              '--asset-dark': getAssetColors(champion).dark
            } as React.CSSProperties}
          >
            <span className="button-label">{champion}</span>
            {(isLoading || pickingAsset === champion) && !playerPick ? (
              <span className="button-status processing">
                <span className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </span>
            ) : (
              <span className="button-status">{playerPick === champion ? 'SELECTED' : 'SELECT'}</span>
            )}
          </button>
          <button
            className={`action-button racer-select ${playerPick === challenger ? 'selected' : ''} ${(isLoading || pickingAsset) ? 'processing' : ''}`}
            onClick={() => handlePickAsset(challenger)}
            disabled={!!playerPick || isLoading || !!pickingAsset}
            style={{
              '--asset-primary': getAssetColors(challenger).primary,
              '--asset-secondary': getAssetColors(challenger).secondary,
              '--asset-dark': getAssetColors(challenger).dark
            } as React.CSSProperties}
          >
            <span className="button-label">{challenger}</span>
            {(isLoading || pickingAsset === challenger) && !playerPick ? (
              <span className="button-status processing">
                <span className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </span>
            ) : (
              <span className="button-status">{playerPick === challenger ? 'SELECTED' : 'SELECT'}</span>
            )}
          </button>
        </div>
      )}

      {/* Results Phase - Won: Show Cash Out / Continue (only if NOT already cashed out) */}
      {isResultsPhase && playerWon && currentPayout && currentSession?.session_status !== 'cashed_out' && (
        <div className="action-buttons decision">
          <button
            className="action-button cash-out"
            onClick={handleCashOut}
            disabled={isLoading || isCashingOut || isContinuing}
          >
            {(isLoading || isCashingOut) ? (
              <span className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
            ) : (
              <>
                <span className="button-label">Cash Out</span>
                <div className="button-multiplier-payout">
                  <span className="multiplier-text">{currentPayout.multiplier}x</span>
                  <span className="separator">•</span>
                  <img 
                    src="/assets/icons/solanaLogoMark.svg" 
                    alt="SOL" 
                    className="button-sol-icon"
                  />
                  <span className="payout-text">{currentPayout.payout}</span>
                </div>
              </>
            )}
          </button>
          {nextPayout ? (
            <button
              className="action-button continue"
              onClick={handleContinue}
              disabled={isLoading || isContinuing || isCashingOut}
            >
              {(isLoading || isContinuing) ? (
                <span className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              ) : (
                <>
                  <span className="button-label">Next Race</span>
                  <div className="button-multiplier-payout">
                    <span className="multiplier-text">{nextPayout.multiplier}x</span>
                    <span className="separator">•</span>
                    <img 
                      src="/assets/icons/solanaLogoMark.svg" 
                      alt="SOL" 
                      className="button-sol-icon"
                    />
                    <span className="payout-text">{nextPayout.payout}</span>
                  </div>
                </>
              )}
            </button>
          ) : (
            <button
              className="action-button cash-out max"
              onClick={handleCashOut}
              disabled={isLoading || isCashingOut}
            >
              {(isLoading || isCashingOut) ? (
                <span className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              ) : (
                <>
                  <span className="button-label">Cash Out</span>
                  <div className="button-multiplier-payout">
                    <span className="multiplier-text">{currentPayout.multiplier}x</span>
                    <span className="separator">•</span>
                    <img 
                      src="/assets/icons/solanaLogoMark.svg" 
                      alt="SOL" 
                      className="button-sol-icon"
                    />
                    <span className="payout-text">{currentPayout.payout}</span>
                  </div>
                  <span className="button-badge">MAX!</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Busted/Cashed Out State: Game ended (lost or cashed out) */}
      {isBusted && payoutLadder && (
        <div className="action-buttons decision">
          <button
            className="action-button info"
            onClick={handleHowItWorks}
          >
            <span className="button-label">How To Play</span>
          </button>
          <button
            className="action-button start"
            onClick={handleStartGame}
            disabled={isLoading || !isAuthenticated || !canAfford(selectedBetAmount)}
            title={!canAfford(selectedBetAmount) && isAuthenticated ? `Insufficient balance (${balance.toFixed(4)} SOL available)` : ''}
          >
            {isLoading ? (
              <span className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
            ) : !isAuthenticated ? (
              <span className="button-label">Connect to Play</span>
            ) : !canAfford(selectedBetAmount) ? (
              <span className="button-label">Insufficient SOL</span>
            ) : (
              <>
                <span className="button-label">Play Again</span>
                <div className="button-multiplier-payout">
                  <span className="multiplier-text">{payoutLadder.ladder[0].multiplier}x</span>
                  <span className="separator">•</span>
                  <img 
                    src="/assets/icons/solanaLogoMark.svg" 
                    alt="SOL" 
                    className="button-sol-icon"
                  />
                  <span className="payout-text">{(selectedBetAmount * payoutLadder.ladder[0].multiplier).toFixed(3)}</span>
                </div>
              </>
            )}
          </button>
        </div>
      )}

      {/* How It Works Modal */}
      <HowItWorksModal 
        isOpen={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
      />
    </div>
  );
};

export default GameSessionControls;

